import { Icons } from "assets/icons";
import React, { useMemo } from "react";
import useWeather from "hooks/useWeather";
import { useTranslation } from "react-i18next";

// WMO code → condition category
const getConditionCategory = (code, temp) => {
  if (code === 0 || code === 1) return "clear";
  if (code === 2 || code === 3) return "cloudy";
  if ([45, 48].includes(code)) return "foggy";
  if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(code)) return "rainy";
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "snowy";
  if ([95, 96, 99].includes(code)) return "stormy";
  if (temp >= 40) return "scorching";
  return "clear";
};

const isDaytime = () => {
  const hour = new Date().getHours();
  return hour >= 6 && hour < 20;
};

// condition + day/night → CSS class
const getBgClass = (category, daytime) => {
  const timeKey = daytime ? "day" : "night";
  const map = {
    clear:     { day: "weather-bg--clear-day",     night: "weather-bg--clear-night" },
    cloudy:    { day: "weather-bg--cloudy-day",    night: "weather-bg--cloudy-night" },
    foggy:     { day: "weather-bg--foggy-day",     night: "weather-bg--foggy-night" },
    rainy:     { day: "weather-bg--rainy-day",     night: "weather-bg--rainy-night" },
    snowy:     { day: "weather-bg--snowy-day",     night: "weather-bg--snowy-night" },
    stormy:    { day: "weather-bg--stormy",        night: "weather-bg--stormy" },
    scorching: { day: "weather-bg--scorching",     night: "weather-bg--clear-night" },
  };
  return map[category]?.[timeKey] ?? "weather-bg--clear-day";
};

const WeatherCard = ({ city = "Riyadh" }) => {
  const { t } = useTranslation();
  const { weather, loading, error } = useWeather(city);
  const { weather: weatherIcon } = Icons || {};

  const daytime = isDaytime();

  const bgClass = useMemo(() => {
    if (!weather) return "weather-bg--clear-day";
    const category = getConditionCategory(weather.weatherCode, weather.temp);
    return getBgClass(category, daytime);
  }, [weather, daytime]);

  if (loading) {
    return (
      <div className="weather_card weather-bg--clear-day">
        <p>{t("Loading weather...")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weather_card weather-bg--clear-day">
        <p>{t("Could not load weather: {{error}}", { error })}</p>
      </div>
    );
  }

  return (
    <div className={`weather_card ${bgClass}`}>
      <div className="weather_card__content">
        <h3>{t(weather.city)}</h3>
        <h2>
          {weather.temp}°C <span>({t(weather.condition)})</span>
        </h2>
        <p className="weather_card__range">
          {t("High")}: {weather.high}°C | {t("Low")}: {weather.low}°C | {t("Rain")}: {weather.rain}%
        </p>
        <p className="weather_card__desc">{t(weather.description)}</p>
      </div>
      <img src={weatherIcon} alt={t("Weather")} width={51} height={51} />
    </div>
  );
};

export default WeatherCard;